/**
 * What a line STARTS WITH once it names a product — the recommended discount,
 * and the campaign inclusions for its quantity.
 *
 * Owner's instructions, 2026-09-12:
 *   *"in products also add a recommended discount amount which should get added
 *    up by default in the line items"*
 *   *"for products when there is a specific quantity then i want to preset the
 *    campaign inclusions … particulars and counts presetted if a product and
 *    quantity is selected in line items"*
 *
 * ⚠️ **ONE COPY FOR THREE GRIDS.** The quote, invoice and deal line editors all
 * seed from this. Three copies would agree the day they were written and drift
 * on the first correction — and what would drift is a figure a customer is
 * billed against, and a set of inclusions printed on their document.
 *
 * ⛔ **NOTHING HERE FETCHES OR WRITES. It decides.** The grids own their own
 * requests and their own draft state; this module answers "given what the
 * product recommends and what the reader has already done, what should change?"
 * — which is the half that must not be reimplemented per grid.
 */

import type { ResolvedInclusionPreset } from '@/lib/types';

/**
 * Whether a line's discount is still the one a preset put there.
 *
 * ⚠️ **THE WHOLE RE-APPLY RULE TURNS ON THIS, and it is not "is the draft
 * empty".** The owner chose *re-apply only if untouched*: counts follow the
 * quantity until somebody edits them by hand, and then the line is theirs. So a
 * seed may overwrite a seed, and may never overwrite a person.
 */
export interface LineDiscountState {
  /** The line's amount discount as typed, or `null` when it discounts by percentage. */
  discountAmount: string | null;
  /** The percentage box, as typed. */
  discount: string;
  /** What this module last seeded, or `null` if it has seeded nothing here. */
  seededDiscountAmount: string | null;
}

/**
 * The discount a line should start at, or `null` for "change nothing".
 *
 * ⛔ **IT NEVER OVERWRITES A PERCENTAGE.** `discount` and `discountAmount` are
 * mutually exclusive — sending both non-zero is a 422 naming `discountAmount` —
 * so seeding an amount onto a line that carries a real percentage would either
 * be refused or silently replace a discount somebody negotiated. A line already
 * discounting by percentage keeps its percentage, and the recommendation is not
 * offered a second way.
 *
 * ⛔ **AND IT NEVER OVERWRITES A TYPED AMOUNT.** The only amount it will replace
 * is one it put there itself (`seededDiscountAmount`), which is what lets the
 * figure follow a product change without ever undoing an edit.
 *
 * ⚠️ **`null` on the product means NO RECOMMENDATION and is not `"0.00"`.** A
 * product recommending `0.00` is recommending nothing off, which is a real
 * instruction and is seeded as such; a product recommending nothing leaves the
 * line alone. `?? '0.00'` anywhere between the wire and here destroys that.
 */
export function recommendedDiscountSeed(
  recommended: string | null,
  state: LineDiscountState,
): string | null {
  if (recommended === null) return null;

  // A real percentage is the person's own choice of mechanism. Leave it.
  const percentage = state.discount.trim();
  if (state.discountAmount === null && percentage !== '' && Number(percentage) !== 0) return null;

  const current = state.discountAmount;

  /*
   * ⛔ **ONCE THIS MODULE HAS SEEDED, "UNTOUCHED" MEANS "STILL EXACTLY WHAT WE
   * SEEDED" — AND AN EMPTY BOX IS NOT A LICENCE TO SEED AGAIN.**
   *
   * The obvious test — *seed whenever the box is empty* — reads a deliberate
   * CLEARING as "never set", so the recommendation reappears the moment somebody
   * empties it, and the box fights them. It is the same failure the derived
   * description avoids with its untouched flag, and the same one the
   * `/projects` cycle default avoids by settling: **clearing has to survive, or
   * the control reads as broken.**
   *
   * Before anything has been seeded, only a genuinely empty box is fair game.
   * Afterwards, the line is ours to update only while it still holds our own
   * figure — which is what lets the amount follow a product change without ever
   * undoing an edit, including an edit that emptied it.
   */
  const untouched =
    state.seededDiscountAmount === null
      ? current === null || current.trim() === ''
      : current === state.seededDiscountAmount;
  if (!untouched) return null;

  return current === recommended ? null : recommended;
}

/** One inclusion on a line, in the shape the three grids' drafts hold. */
export interface LineInclusionDraft {
  inclusionId: number;
  quantity: number;
}

/**
 * The inclusions a line should start with, or `null` for "change nothing".
 *
 * ⛔ **`band === null` PRESETS NOTHING — never the nearest band, never an empty
 * set.** A gap in the ladder is a quantity an administrator has not described,
 * and the honest answer is to leave the line as it is. Returning `[]` there
 * would CLEAR a set the reader may have chosen, because an inclusions write is
 * a whole-set REPLACE.
 *
 * ⚠️ **A band WITH an empty list is a different fact and is honoured.** It says
 * "this range presets nothing", which is a decision somebody made, so a line
 * following the preset is emptied to match. That is why `resolved.band` has to
 * be read and not just `resolved.inclusions`.
 *
 * ⛔ **IT NEVER OVERWRITES A HAND-EDITED SET.** `seeded` is what this module last
 * put on the line; if the line no longer holds exactly that, somebody has
 * changed it and it is theirs. Compared by CONTENT rather than by identity,
 * because the draft is rebuilt on every render.
 */
export function inclusionPresetSeed(
  resolved: ResolvedInclusionPreset | null,
  current: readonly LineInclusionDraft[],
  seeded: readonly LineInclusionDraft[] | null,
): LineInclusionDraft[] | null {
  if (resolved === null) return null;
  if (resolved.band === null) return null;

  /*
   * ⛔ **SAME RULE AS THE DISCOUNT, AND THE SAME TRAP.** *Seed whenever the set
   * is empty* reads a deliberate CLEARING as "never chosen", so the preset
   * reappears the moment somebody unticks the last inclusion — on a set that
   * prints on a customer's document. Before anything has been seeded only a
   * genuinely empty set is fair game; afterwards the line is ours to update only
   * while it still holds exactly what we seeded.
   */
  const untouched =
    seeded === null ? current.length === 0 : sameInclusions(current, seeded);
  if (!untouched) return null;

  const next = resolved.inclusions.map((entry) => ({
    inclusionId: entry.id,
    quantity: entry.quantity,
  }));

  return sameInclusions(current, next) ? null : next;
}

/**
 * Set equality on (id, quantity), order-independent.
 *
 * ⚠️ **Order-independent deliberately.** The server returns a band's entries in
 * the inclusion master's own `sortOrder`, and a draft holds them in the order
 * the reader ticked; comparing as sequences would report every line as
 * hand-edited the moment the two disagreed, and the preset would stop following
 * the quantity with nothing on screen to explain it.
 */
function sameInclusions(
  a: readonly LineInclusionDraft[],
  b: readonly LineInclusionDraft[],
): boolean {
  if (a.length !== b.length) return false;
  const byId = new Map(b.map((entry) => [entry.inclusionId, entry.quantity]));
  return a.every((entry) => byId.get(entry.inclusionId) === entry.quantity);
}
